import React from 'react';
import { formatPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import { useInView } from 'react-intersection-observer';
import * as Sentry from '@sentry/react';
import { faCcVisa, faCcMastercard, faCcDiscover, faCcAmex } from '@fortawesome/free-brands-svg-icons';
import { giftya_base_url } from '../var';


const getFullMonth = (m) => {
    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    return monthNames[m-1];
};

export function useInViewInit(props) {
    let inView = true;
    let ref = React.useRef();

    try {
        const useInViewInit = useInView(props);
        inView = useInViewInit.inView;
        ref = useInViewInit.ref;
    } catch (error) {
        Sentry.addBreadcrumb({
            category: "app",
            message: "Intersection observer not available",
            level: 'info',
        });
    }

    return {inView, ref};
}

export function createActionTypes(base, actions = []) {
  return actions.reduce((acc, type) => {
    acc[type] = `${base}_${type}`;

    return acc
  }, {})
}

export function createAction(type, data = {}) {
  return { type, payload: data }
}

export function mapStateAbbreviations(state) {
    const states = {
        'Alabama': 'AL',
        'Alaska': 'AK',
        'Arizona': 'AZ',
        'Arkansas': 'AR',
        'California': 'CA',
        'Colorado': 'CO',
        'Connecticut': 'CT',
        'Delaware': 'DE',
        'District of Columbia': 'DC',
        'Florida': 'FL',
        'Georgia': 'GA',
        'Hawaii': 'HI',
        'Idaho': 'ID',
        'Illinois': 'IL',
        'Indiana': 'IN',
        'Iowa': 'IA',
        'Kansas': 'KS',
        'Kentucky': 'KY',
        'Louisiana': 'LA',
        'Maine': 'ME',
        'Maryland': 'MD',
        'Massachusetts': 'MA',
        'Michigan': 'MI',
        'Minnesota': 'MN',
        'Mississippi': 'MS',
        'Missouri': 'MO',
        'Montana': 'MT',
        'Nebraska': 'NE',
        'Nevada': 'NV',
        'New Hampshire': 'NH',
        'New Jersey': 'NJ',
        'New Mexico': 'NM',
        'New York': 'NY',
        'North Carolina': 'NC',
        'North Dakota': 'ND',
        'Ohio': 'OH',
        'Oklahoma': 'OK',
        'Oregon': 'OR',
        'Pennsylvania': 'PA',
        'Rhode Island': 'RI',
        'South Carolina': 'SC',
        'South Dakota': 'SD',
        'Tennessee': 'TN',
        'Texas': 'TX',
        'Utah': 'UT',
        'Vermont': 'VT',
        'Virginia': 'VA',
        'Washington': 'WA',
        'West Virginia': 'WV',
        'Wisconsin': 'WI',
        'Wyoming': 'WY'
    };
    return states[state] ? states[state] : state;
}

export const serialize = ( obj ) => {
    return '?' + Object.keys(obj).reduce(function(a, k){
        a.push(k + '=' + encodeURIComponent(obj[k]));
        return a;
    }, []).join('&');
};

export const slugify = (name = '') => {
    return name.replace(/'|,|\./g, '').replace(/&/g, 'and').replace(/\s+/g, '-').toLowerCase();
};

export const cdnBase = (uri) => {
    return `https://d2kn7nb9ubajli.cloudfront.net/${uri}`;
};

export const ucWords = (string) => {
    if (!string) { return string; }
    let str = string.toLowerCase();
    return str.replace(/(^([a-zA-Z\p{M}]))|([ -][a-zA-Z\p{M}])/g,
        function (s) {
            return s.toUpperCase();
        }
    );
};

export const formatPhone = (phone) => {
    return formatPhoneNumber(`+${phone}`);
};

export const formatDate = (date, format = 'yyyy-mm-dd', utc = true) => {
    if (!date){
        return '';
    }
    const d = new Date(date);
    let year;
    let m;
    let day;
    if (utc) {
        year = d.getUTCFullYear();
        m = '' + (d.getUTCMonth() + 1);
        day = '' + d.getUTCDate();
    } else {
        year = d.getFullYear();
        m = '' + (d.getMonth() + 1);
        day = '' + d.getDate();
    }
    if (m.length < 2)
        m = `0${m}`;
    if (day.length < 2)
        day = '0' + day;

    if (format === 'M d, Y') {
        return `${getFullMonth(m)} ${day}, ${year}`;
    }

    if (format === 'yyyy-mm-dd') {
        return [year, m, day].join('-');
    }

    if (format === 'mm/dd/yyyy') {
        return [m, day, year].join('/');
    }
};

export const getRedemptionOption = (gift) => {
    return [
        ...(gift.merchant_visa_enabled ? ['visa'] : []),
        ...(gift.merchant_mastercard_enabled ? ['mastercard'] : []),
    ];
};

export const sendExceptionEvent = (response) => {
    if (!response) { return; }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'exception',
        code: response.data.code,
        message: response.data.message,
        url: response.config.url
    });
};

export const contactName = (gift) => {
    // If recipient name is set, return that
    if (gift.recipient && gift.recipient.first_name && gift.recipient.last_name) {
        return `${gift.recipient.first_name} ${gift.recipient.last_name}`;
    }

    if (gift.identity_value) {
        if (gift.identity_value.includes('@') || !isPossiblePhoneNumber(`+${gift.identity_value}`)) {
            return gift.identity_value;
        }

        return formatPhoneNumber(`+${gift.identity_value}`);
    }

    return '';
};

export const formatConfirmationCode = (value) => {
    value = value.replace(/\D/g, '');
    const length = value.length;

    if (length <= 3) {
        return value;
    }
    return `${value.substring(0,3)}-${value.substring(3,6)}`;
};

export const formatIdentity = (identityValue) => {
    if (!identityValue || identityValue.includes('@') || !isPossiblePhoneNumber(`+${identityValue}`)) {
        return identityValue;
    }

    return formatPhoneNumber(`+${identityValue}`);
};

export const identityTypeVerbiage = (gift) => {
    if (!gift.identity_type) {
        return '';
    }

    return gift.identity_type === 'email' ? 'email address' : 'phone number';
};

export const locationToQueryString = (location) => {
    const retStr = `${location.city}-${location.state}`;
    return retStr.replace(' ', '-').toLowerCase();
};

export const validateEmail = (email) => {
    return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
}

export const validatePhoneNumber = (phoneNumber = '') => {
    return `1${phoneNumber.replace(/\D/g, '').replace(/^1/, '')}`.length === 11;
}

export const maskPhone = (value, stripNonNumeric = true) => {
    if (stripNonNumeric) {
        value = value.replace(/\D/g, '');
    }
    value = value.replace(/^1/, "");

    let formattedValue;
    const length = value.length;

    if (length === 0) {
        formattedValue = ``;
    }

    if (length > 0 && length <= 3) {
        formattedValue = `(${value.substring(0,3)}`;
    }

    if (length > 3 && length <= 6) {
        formattedValue = `(${value.substring(0,3)}) ${value.substring(3)}`;
    }

    if (length > 6) {
        formattedValue = `(${value.substring(0,3)}) ${value.substring(3,6)}-${value.substring(6,10)}`;
    }
    return formattedValue;
};

export const maskCurrency = (value) => {
    value = value.replace(/([^0-9.])/g, '');
    value = value.replace(/^(\d+)(\.\d{2})\d*$/g, "$1$2");
    return value;
};

export const maskZip = (value) => {
    return value.replace(/\D/g, '').substring(0, 5);
};

export const maskCC = (value) => {
    let type = null;
    value = value.replace(/\D/g, '').substring(0,16);

    let formattedValue = value;

    if (value.length > 4) {
        formattedValue = `${value.substring(0,4)}-${value.substring(4)}`;
    }

    if (value.length > 8) {
        formattedValue = `${value.substring(0,4)}-${value.substring(4,8)}-${value.substring(8)}`;
    }

    if (value.length > 12) {
        formattedValue = `${value.substring(0,4)}-${value.substring(4,8)}-${value.substring(8,12)}-${value.substring(12)}`;
    }

    if (value.length === 14) {
        formattedValue = `${value.substring(0,4)}-${value.substring(4,10)}-${value.substring(10)}`;
    }

    if (value.length === 15) {
        formattedValue = `${value.substring(0,4)}-${value.substring(4,10)}-${value.substring(10)}`;
    }

    if (value.length > 15) {
        formattedValue = `${value.substring(0,4)}-${value.substring(4,8)}-${value.substring(8,12)}-${value.substring(12,16)}`;
    }

    const iconSet = [];

    if (value === '' || (/^4/.test(value) && value.length === 16)) {
        type = 'Visa';
        iconSet.push(faCcVisa);
    }

    if (value === '' || (/^(5[1-5]|2[22100‑72099])/.test(value) && value.length === 16)) {
        type = 'Mastercard';
        iconSet.push(faCcMastercard);
    }

    if (value === '' || (/^(\d{4})(\d{6})(\d{5})$/.test(value) && value.length === 15)) {
        type = 'American Express';
        iconSet.push(faCcAmex);
    }

    if (value === '' || (/^(6011|65|62[2126‑2925]|64[4-9])/.test(value) && value.length === 16)) {
        type = 'Discover';
        iconSet.push(faCcDiscover);
    }

    return {
        formattedValue,
        iconSet,
        type: value.length > 13 ? type : null
    };
};

export const insertRecaptcha = (render = false, onload = 'onloadCallback') => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?${onload ? `onload=${onload}` : ''}${onload && render ? '&' : ''}${render ? `render=${window.recaptcha_site_key}` : ''}`;
    document.getElementsByTagName("head")[0].appendChild(script);
    return script;
};

export const convertCcType = (ccType) => {
    let conversion = ccType;
    switch (ccType) {
        case 'Discover': conversion = 'discover'; break;
        case 'discover': conversion = 'Discover'; break;
        case 'American Express': conversion = 'american_express'; break;
        case 'american_express': conversion = 'American Express'; break;
        case 'Visa': conversion = 'visa'; break;
        case 'visa': conversion = 'Visa'; break;
        case 'Mastercard': conversion = 'master_card'; break;
        case 'master_card': conversion = 'Mastercard'; break;
        default: conversion = ccType; break;
    }
    return conversion;
};

export const getBuyPageUrl = ({place, query, page, latLng}) => {
    const url = new URL(giftya_base_url);
    url.pathname = '/buy';

    if (latLng && place) {
        url.pathname = `/${slugify(place)}`;
        url.searchParams.set('lat', latLng.lat);
        url.searchParams.set('lng', latLng.lng);
    }
    if (query) {
        url.searchParams.set('q', query);
    }
    if (page) {
        url.searchParams.set('p', page);
    }
    return url;
};

/**
 * addressComponents will be like Array(5)
 0:
 long_name: "Timonium"
 short_name: "Timonium"
 types: (2) ['locality', 'political']
 1:
 long_name: "Baltimore County"
 short_name: "Baltimore County"
 types: (2) ['administrative_area_level_2', 'political']
 2:
 long_name: "Maryland"
 short_name: "MD"
 types: (2) ['administrative_area_level_1', 'political']
 3: {long_name: 'United States', short_name: 'US', types: Array(2)}
 4:
 long_name: "21093"
 short_name: "21093"
 types: ['postal_code']
 * We only want Pittsburgh, PA to match our city routes
 * @param addressComponents
 * @returns string
 */
export const cleanCityName = (addressComponents) => {
    const parts = {
        city: '',
        state: ''
    };

    for (let i in addressComponents) {
        const component = addressComponents[i];
        if (component.types.includes('locality')) {
            parts.city = component.short_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
            parts.state = component.short_name;
        }
    }
    return `${parts.city}, ${parts.state}`;
}

export const getStripePartsFromClientSecret = (clientSecret) => {
    const parts = clientSecret.split('_secret_');
    return {
        'payment_intent': parts[0],
        'secret': parts[1]
    };
}